if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        this.addEventListener('change', (event) => {
          const target = this.getInputForEventTarget(event.target);
          this.updateSelectedSwatchValue(event);
          // Must run before selectedOptionValues is read, so the request carries
          // the corrected size rather than a combination that has no variant.
          this.repairUnavailableSize(target);
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
            },
          });
        });
      }

      // Not cached: this element is replaced wholesale on every server re-render,
      // and a stale matrix would be worse than the tiny parse cost.
      get variantMatrix() {
        const node = this.querySelector('[data-variant-matrix]');
        if (!node) return null;
        try {
          const parsed = JSON.parse(node.textContent);
          return Array.isArray(parsed) ? parsed : null;
        } catch (error) {
          return null;
        }
      }

      get optionGroups() {
        return Array.from(this.querySelectorAll(':scope > .product-form__input'));
      }

      getGroupValue(group) {
        const checked = group.querySelector('input[type="radio"]:checked');
        if (checked) return checked.value;
        const select = group.querySelector('select');
        return select ? select.value : null;
      }

      setGroupValue(group, value) {
        const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find(
          (input) => input.value === value
        );
        if (radio) {
          // Set .checked directly: dispatching change here would re-enter this handler.
          radio.checked = true;
        } else {
          const select = group.querySelector('select');
          const option = select && Array.from(select.options).find((opt) => opt.value === value);
          if (!option) return false;
          // selectedOptionValues reads the `selected` ATTRIBUTE, not the property.
          Array.from(select.options).forEach((opt) => opt.removeAttribute('selected'));
          option.setAttribute('selected', 'selected');
          select.value = value;
        }

        // Keep the visible labels honest until the server re-render lands.
        const trigger = group.querySelector('[data-selected-value]');
        if (trigger) trigger.textContent = value;
        const legend = group.querySelector('[data-selected-swatch-value]');
        if (legend) legend.textContent = value;
        const badge = group.querySelector('[data-soldout-badge]');
        if (badge) badge.classList.add('hidden');

        return true;
      }

      /**
       * Selecting a colour that doesn't stock the currently selected size used to
       * request a combination with no variant at all, so the whole product read as
       * unavailable. Instead, keep the option the shopper just clicked and move the
       * size to one that's actually in stock for it.
       *
       * Deliberately one-directional: changing the SIZE never rewrites the colour,
       * so clicking a sold-out size still shows the normal sold-out state.
       */
      repairUnavailableSize(changedTarget) {
        // Never let this throw: an exception here would stop the picker publishing
        // its change event at all, which is far worse than the bug being fixed.
        if (!changedTarget || typeof changedTarget.closest !== 'function') return;

        const matrix = this.variantMatrix;
        if (!matrix || !matrix.length) return;

        const groups = this.optionGroups;
        const sizeIndex = groups.findIndex((group) => group.dataset.optionRole === 'size');
        // No size option on this product (or it isn't flagged) — leave everything alone.
        if (sizeIndex === -1 || groups.length < 2) return;

        const changedGroup = changedTarget.closest('.product-form__input');
        if (!changedGroup || changedGroup === groups[sizeIndex]) return;

        const desired = groups.map((group) => this.getGroupValue(group));
        if (desired.length !== groups.length || desired.some((value) => value === null)) return;

        const matches = (combo) =>
          combo.length === desired.length && combo.every((value, index) => value === desired[index]);
        // Already a buyable combination: change nothing.
        if (matrix.some(matches)) return;

        // Same everything else, any size that's in stock. Matrix order follows the
        // merchant's variant order, so this picks their first available size.
        const replacement = matrix.find(
          (combo) =>
            combo.length === desired.length &&
            combo.every((value, index) => index === sizeIndex || value === desired[index])
        );
        // Nothing available in this colour at all — fall through to the theme's
        // existing sold-out handling rather than inventing a selection.
        if (!replacement) return;

        this.setGroupValue(groups[sizeIndex], replacement[sizeIndex]);
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            .removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}
